import { Suspense, lazy, useEffect } from 'react';

import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';

import {
    getCurrentAppData,
    useApplicationStore,
} from '@App/stores/applicationStore';
import ProtectedRoute from '@Components/Routes/ProtectedRoute';
import SharedLayout from '@Layouts/SharedLayout';
import { getCookieIds } from '@Shared/helpers/cookie';
import { trackEvent } from '@src/utils/analytics';

import '@selina-finance/ui/build/fonts.css';

const BankDetailsPage = lazy(
    () => import('@Pages/BankDetails/BankDetailsPage'),
);
const ConcurrentApplicationsPage = lazy(
    () =>
        import('@Pages/ConcurrentApplicationsPage/ConcurrentApplicationsPage'),
);
const DeclinedPage = lazy(() => import('@Pages/DeclinedPage/DeclinedPage'));
const DocsListPage = lazy(() => import('@Pages/DocsListPage/DocsListPage'));
const DocsPage = lazy(() => import('@Pages/DocsPage/DocsPage'));
const ExistingLoanPage = lazy(
    () => import('@Pages/ExistingLoanPage/ExistingLoanPage'),
);
const ExpiredPage = lazy(() => import('@Pages/ExpiredPage/ExpiredPage'));
const LoadingPage = lazy(() => import('@Pages/LoadingPage'));
const SubmitPage = lazy(() => import('@Pages/SubmitPage/SubmitPage'));
const VerifyPage = lazy(() => import('@Pages/VerifyPage/VerifyPage'));
const AppDownloadRedirectPage = lazy(
    () => import('@Pages/AppDownloadRedirectPage/AppDownloadRedirectPage'),
);
const ApprovedPage = lazy(() => import('@Pages/ApprovedPage/ApprovedPage'));
const NotFoundPage = lazy(() => import('@Pages/NotFoundPage/NotFoundPage'));

const App = () => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const currentAppId = useApplicationStore((state) => state.currentAppId);
    const setCurrentAppId = useApplicationStore(
        (state) => state.setCurrentAppId,
    );

    const bankDetailsComplete = useApplicationStore(
        (state) => getCurrentAppData(state).bankDetailsComplete,
    );
    const verifyComplete = useApplicationStore(
        (state) => getCurrentAppData(state).verifyComplete,
    );
    const docsComplete = useApplicationStore(
        (state) => getCurrentAppData(state).docsComplete,
    );

    useApplicationStore((state) => getCurrentAppData(state).decisionStatus);

    useEffect(() => {
        const { appId } = getCookieIds();
        if (appId && appId !== currentAppId) {
            setCurrentAppId(appId);
        }
    }, [setCurrentAppId, currentAppId]);

    useEffect(() => {
        if (navigationType === 'POP') {
            trackEvent('back_navigation_attempted', {
                path: location.pathname,
            });
        }
    }, [location.pathname, navigationType]);

    const getFurthestCompletedRoute = () => {
        if (docsComplete) return '/submit';
        if (verifyComplete) return '/docs';
        if (bankDetailsComplete) return '/verify';
        return '/bank-details';
    };

    const renderDocsRoute = (element: JSX.Element) => (
        <ProtectedRoute
            isPrevStepComplete={verifyComplete}
            prevStepRedirectTo='/verify'
            isStepComplete={docsComplete}
            stepCompleteRedirectTo={getFurthestCompletedRoute()}
        >
            {element}
        </ProtectedRoute>
    );

    return (
        <Routes>
            <Route
                element={
                    <Suspense>
                        <SharedLayout />
                    </Suspense>
                }
            >
                <Route
                    path='/'
                    element={
                        <ProtectedRoute>
                            <Navigate
                                to={getFurthestCompletedRoute()}
                                replace
                            />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path='/bank-details'
                    element={
                        <ProtectedRoute
                            isStepComplete={bankDetailsComplete}
                            stepCompleteRedirectTo={getFurthestCompletedRoute()}
                        >
                            <BankDetailsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path='/verify'
                    element={
                        <ProtectedRoute
                            isPrevStepComplete={bankDetailsComplete}
                            prevStepRedirectTo='/bank-details'
                            isStepComplete={verifyComplete}
                            stepCompleteRedirectTo={getFurthestCompletedRoute()}
                        >
                            <VerifyPage />
                        </ProtectedRoute>
                    }
                />
                <Route path='/docs' element={renderDocsRoute(<DocsPage />)} />
                <Route
                    path='/docs/list'
                    element={renderDocsRoute(<DocsListPage />)}
                />
                <Route
                    path='/submit'
                    element={
                        <ProtectedRoute
                            isPrevStepComplete={docsComplete}
                            prevStepRedirectTo='/docs'
                        >
                            <SubmitPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path='/approved'
                    element={
                        <ProtectedRoute
                            expectedDecisionStatus='approved'
                            decisionRedirectTo={getFurthestCompletedRoute()}
                        >
                            <ApprovedPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path='/declined'
                    element={
                        <ProtectedRoute
                            expectedDecisionStatus='declined'
                            decisionRedirectTo={getFurthestCompletedRoute()}
                        >
                            <DeclinedPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path='/concurrent-applications'
                    element={<ConcurrentApplicationsPage />}
                />
                <Route
                    path='/existing-loan'
                    element={
                        <ProtectedRoute
                            expectedDecisionStatus='existing-loan'
                            decisionRedirectTo={getFurthestCompletedRoute()}
                        >
                            <ExistingLoanPage />
                        </ProtectedRoute>
                    }
                />
                <Route path='/expired' element={<ExpiredPage />} />
                <Route
                    path='/app-download'
                    element={<AppDownloadRedirectPage />}
                />
                <Route path='/not-found' element={<NotFoundPage />} />
                {/* may not be needed */}
                <Route path='/loading' element={<LoadingPage />} />
                <Route
                    path='*'
                    element={<Navigate to='/not-found' replace />}
                />
            </Route>
        </Routes>
    );
};

export default App;
